import { ulid } from 'ulid';
import type { CommonEvent, EventName, EventPropsMap } from './schema';
import { SCHEMA_VERSION, createEvent, validateEvent, CommonEventSchema } from './schema';
import {
    getAnonId,
    getSessionId,
    getUserId,
    getABBuckets,
    getViewId,
    setUserId as setUserIdInternal,
    setABBuckets,
    initIdManager,
    generateIntentId,
    setSearchIntentId,
    getSearchIntentId,
    clearSearchIntentId
} from './ids';
import {
    initConfig,
    getConfig,
    updateConfig,
    shouldSample,
    checkAndIncrementLimit,
    type AnalyticsConfig
} from './config';
import {
    setDebugMode,
    debugLogEvent,
    debugLogBatch,
    debugLogError,
    debugLogOfflineQueue,
    debugLogSampling,
    debugLogLimit,
    debugLogSchemaError,
    setupGlobalDebug
} from './debug';
import { isCurrentUserBot } from './bot-detector';

/**
 * 이벤트 버퍼 및 전송 관리
 */
class EventBuffer {
    private events: CommonEvent[] = [];
    private flushTimeout: NodeJS.Timeout | null = null;
    private isOnline = true;
    private offlineQueue: CommonEvent[] = [];

    constructor() {
        this.setupOnlineListener();
        this.loadOfflineQueue();
    }

    /**
 * 온라인/오프라인 상태 감지
 */
    private setupOnlineListener(): void {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

        this.isOnline = navigator.onLine;

        window.addEventListener('online', () => {
            this.isOnline = true;
            this.processOfflineQueue();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
        });
    }

    /**
     * 오프라인 큐 로딩
     */
    private loadOfflineQueue(): void {
        if (typeof localStorage === 'undefined') return;

        const config = getConfig();
        try {
            const stored = localStorage.getItem(config.offlineQueue.storageKey);
            if (stored) {
                this.offlineQueue = JSON.parse(stored);
                debugLogOfflineQueue('restore', this.offlineQueue.length);
            }
        } catch (error) {
            // 파싱 실패 시 큐 초기화
            this.clearOfflineQueue();
        }
    }

    /**
     * 오프라인 큐 저장
     */
    private saveOfflineQueue(): void {
        if (typeof localStorage === 'undefined') return;

        const config = getConfig();
        try {
            localStorage.setItem(config.offlineQueue.storageKey, JSON.stringify(this.offlineQueue));
        } catch (error) {
            // 저장 실패 시 오래된 이벤트부터 제거
            while (this.offlineQueue.length > 0) {
                this.offlineQueue.shift();
                try {
                    localStorage.setItem(config.offlineQueue.storageKey, JSON.stringify(this.offlineQueue));
                    break;
                } catch {
                    continue;
                }
            }
        }
    }

    /**
     * 오프라인 큐 처리
     */
    private async processOfflineQueue(): Promise<void> {
        if (this.offlineQueue.length === 0) return;

        const events = [...this.offlineQueue];
        this.offlineQueue = [];
        this.saveOfflineQueue();

        try {
            await this.sendEvents(events);
            debugLogOfflineQueue('restore', events.length);
        } catch (error) {
            // 재전송 실패 시 다시 큐에 추가 (최대 한도 내에서)
            const config = getConfig();
            const remainingSpace = config.offlineQueue.maxItems - this.offlineQueue.length;
            const eventsToKeep = events.slice(-remainingSpace);

            this.offlineQueue.unshift(...eventsToKeep);
            this.saveOfflineQueue();
        }
    }

    /**
     * 이벤트 추가
     */
    add(event: CommonEvent): void {
        const config = getConfig();

        // 스키마 검증 (디버그 모드에서만)
        if (config.debug) {
            try {
                CommonEventSchema.parse(event);
            } catch (error) {
                debugLogSchemaError(event.event_name, error);
                return;
            }
        }

        // 디버그 로그
        debugLogEvent(event);

        this.events.push(event);

        // 버퍼 크기 확인
        if (this.events.length >= config.bufMax) {
            this.flush();
        } else {
            this.scheduleFlush();
        }
    }

    /**
     * 플러시 스케줄링
     */
    private scheduleFlush(): void {
        if (this.flushTimeout) return;

        const config = getConfig();
        this.flushTimeout = setTimeout(() => {
            this.flush();
        }, config.bufMs);
    }

    /**
     * 즉시 플러시
     */
    async flush(): Promise<void> {
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = null;
        }

        if (this.events.length === 0) return;

        const events = [...this.events];
        this.events = [];

        if (this.isOnline) {
            try {
                await this.sendEvents(events);
            } catch (error) {
                // 온라인이지만 전송 실패 시 오프라인 큐에 추가
                this.addToOfflineQueue(events);
            }
        } else {
            // 오프라인 시 큐에 저장
            this.addToOfflineQueue(events);
        }
    }

    /**
     * 오프라인 큐에 이벤트 추가
     */
    private addToOfflineQueue(events: CommonEvent[]): void {
        const config = getConfig();

        // 최대 큐 크기 내에서 추가
        const availableSpace = config.offlineQueue.maxItems - this.offlineQueue.length;
        const eventsToAdd = events.slice(-availableSpace);

        this.offlineQueue.push(...eventsToAdd);
        this.saveOfflineQueue();

        debugLogOfflineQueue('store', eventsToAdd.length);
    }

    /**
     * 이벤트 전송
     */
    private async sendEvents(events: CommonEvent[]): Promise<void> {
        const config = getConfig();
        const chunks = this.chunkEvents(events, config.maxPayloadBytes);

        for (const chunk of chunks) {
            await this.sendChunk(chunk);
        }
    }

    /**
     * 페이로드 크기별 이벤트 분할
     */
    private chunkEvents(events: CommonEvent[], maxBytes: number): CommonEvent[][] {
        const chunks: CommonEvent[][] = [];
        let currentChunk: CommonEvent[] = [];
        let currentSize = 0;

        for (const event of events) {
            const eventSize = JSON.stringify(event).length;

            // 단일 이벤트가 최대 크기를 초과하는 경우 스킵
            if (eventSize > maxBytes) {
                console.warn('Analytics: Event too large, skipping:', event.event_name);
                continue;
            }

            // 현재 청크에 추가했을 때 크기 초과 시 새 청크 시작
            if (currentChunk.length > 0 && (currentSize + eventSize) > maxBytes) {
                chunks.push(currentChunk);
                currentChunk = [event];
                currentSize = eventSize;
            } else {
                currentChunk.push(event);
                currentSize += eventSize;
            }
        }

        if (currentChunk.length > 0) {
            chunks.push(currentChunk);
        }

        return chunks;
    }

    /**
 * 청크 전송
 */
    private async sendChunk(events: CommonEvent[]): Promise<void> {
        const config = getConfig();

        debugLogBatch(events, config.endpoint);

        const payload = JSON.stringify(events);

        // sendBeacon 시도 (페이지 이탈 시 우선)
        if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            const sent = navigator.sendBeacon(config.endpoint, blob);

            if (sent) {
                return;
            }
        }

        // fetch 폴백 (keepalive 옵션)
        if (typeof fetch !== 'undefined') {
            const response = await fetch(config.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: payload,
                keepalive: true,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } else {
            throw new Error('fetch is not available');
        }
    }

    /**
     * 오프라인 큐 클리어
     */
    clearOfflineQueue(): void {
        this.offlineQueue = [];
        const config = getConfig();

        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(config.offlineQueue.storageKey);
        }

        debugLogOfflineQueue('clear', 0);
    }

    /**
 * 페이지 언로드 시 강제 플러시
 */
    flushOnUnload(): void {
        if (this.events.length === 0) return;

        const config = getConfig();
        const payload = JSON.stringify(this.events);

        // sendBeacon으로 최후 전송 시도
        if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(config.endpoint, blob);
        }

        this.events = [];
    }
}

/**
 * Analytics 클라이언트 싱글톤
 */
class AnalyticsClient {
    private buffer: EventBuffer;
    private initialized = false;
    private isBot = false;

    constructor() {
        this.buffer = new EventBuffer();
        this.setupUnloadHandlers();
    }

    /**
 * 페이지 언로드 핸들러 설정
 */
    private setupUnloadHandlers(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        const handleUnload = () => {
            this.buffer.flushOnUnload();
        };

        window.addEventListener('beforeunload', handleUnload);
        window.addEventListener('pagehide', handleUnload);

        // visibility API 지원 시
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.buffer.flushOnUnload();
                }
            });
        }
    }

    /**
     * 초기화
     */
    init(config: Partial<AnalyticsConfig> = {}): void {
        if (this.initialized) {
            console.warn('Analytics already initialized');
            return;
        }

        // 🤖 봇 탐지
        this.isBot = isCurrentUserBot();

        // 설정 초기화
        initConfig(config);
        const finalConfig = getConfig();

        // 디버그 모드 설정
        setDebugMode(finalConfig.debug);

        // ID 관리자 초기화
        initIdManager();

        // 전역 디버그 도구 설정
        setupGlobalDebug(
            () => this.flush(),
            () => this.getSessionInfo(),
            () => this.buffer.clearOfflineQueue()
        );

        this.initialized = true;

        if (finalConfig.debug) {
            console.log('🚀 Analytics initialized', {
                ...finalConfig,
                isBot: this.isBot,
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'
            });
        }

        if (this.isBot) {
            console.log('🤖 Bot detected - Analytics tracking disabled');
        }
    }

    /**
     * 이벤트 추적
     */
    track<T extends EventName>(
        eventName: T,
        props: EventPropsMap[T] = {} as EventPropsMap[T]
    ): void {
        if (!this.initialized) {
            console.warn('Analytics not initialized. Call initAnalytics() first.');
            return;
        }

        // 🤖 봇 필터링
        if (this.isBot) {
            const config = getConfig();
            if (config.debug) {
                console.log(`[Analytics] Event ${eventName} ignored (bot detected)`);
            }
            return;
        }

        // 🔍 search_open 자동 처리: intent_id 생성 및 저장
        if (eventName === 'search_open') {
            const intentId = generateIntentId();
            setSearchIntentId(intentId);
            (props as any).intent_id = intentId;
        }

        // 🔍 search_performed 자동 처리: 저장된 intent_id 자동 연결
        if (eventName === 'search_performed') {
            const savedIntentId = getSearchIntentId();
            if (savedIntentId && !(props as any).intent_id) {
                (props as any).intent_id = savedIntentId;
            }
        }

        const config = getConfig();
        const sessionId = getSessionId();

        // 샘플링 체크
        const eventType = this.getEventType(eventName);
        if (eventType && !shouldSample(eventType)) {
            debugLogSampling(eventName, false, config.sampling[eventType]);
            return;
        }

        // 세션별 한도 체크
        const limitType = this.getLimitType(eventName);
        if (limitType && !checkAndIncrementLimit(limitType, sessionId)) {
            debugLogLimit(limitType, false, 0, config.limits[`${limitType}PerSession` as keyof typeof config.limits]);
            return;
        }

        // 공통 필드 생성
        const commonFields = this.createCommonFields();

        // 이벤트 생성
        const event = createEvent(eventName, props, commonFields);

        // 버퍼에 추가
        this.buffer.add(event);
    }

    /**
     * 이벤트 타입 추론 (샘플링용)
     */
    private getEventType(eventName: EventName): keyof AnalyticsConfig['sampling'] | null {
        if (eventName.includes('impression')) return 'impression';
        if (eventName.includes('click') || eventName.includes('tab_click')) return 'minorClick';
        return null;
    }

    /**
     * 한도 타입 추론
     */
    private getLimitType(eventName: EventName): 'impression' | 'minorInteraction' | null {
        if (eventName.includes('impression')) return 'impression';
        if (eventName.includes('click') || eventName.includes('tab_click')) return 'minorInteraction';
        return null;
    }

    /**
     * 공통 필드 생성
     */
    private createCommonFields(): Omit<CommonEvent, 'event_name' | 'props'> {
        const now = Date.now();

        return {
            schema_version: SCHEMA_VERSION,
            event_id: ulid(),
            client_ts: now,
            anon_id: getAnonId(),
            user_id: getUserId(),
            session_id: getSessionId(),
            view_id: getViewId() || ulid(), // view_id가 없으면 임시로 생성 (화면 추적 미초기화 상태)
            page: this.getPageInfo(),
            utm: this.getUTMInfo(),
            device: this.getDeviceInfo(),
            exp: { ab: getABBuckets() },
            perf: this.getPerfInfo(),
        };
    }

    /**
 * 페이지 정보 수집
 */
    private getPageInfo() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return { url: '', path: '' };
        }

        const url = window.location.href;
        const path = window.location.pathname;
        const title = document.title || null;
        const referrer = document.referrer || null;

        let refDomain = null;
        if (referrer) {
            try {
                refDomain = new URL(referrer).hostname;
            } catch {
                // URL 파싱 실패 시 무시
            }
        }

        return { url, path, title, referrer, ref_domain: refDomain };
    }

    /**
 * UTM 정보 수집
 */
    private getUTMInfo() {
        if (typeof window === 'undefined') {
            return {
                source: null,
                medium: null,
                campaign: null,
                term: null,
                content: null,
            };
        }

        const urlParams = new URLSearchParams(window.location.search);

        // click_id 파라미터가 있는지 확인
        const clickIdParams = {
            gclid: urlParams.get('gclid'),
            gbraid: urlParams.get('gbraid'),
            wbraid: urlParams.get('wbraid'),
            n_media: urlParams.get('n_media'),
            msclkid: urlParams.get('msclkid'),
        };

        const hasClickId = Object.values(clickIdParams).some(value => value !== null);

        return {
            source: urlParams.get('utm_source'),
            medium: urlParams.get('utm_medium'),
            campaign: urlParams.get('utm_campaign'),
            term: urlParams.get('utm_term'),
            content: urlParams.get('utm_content'),
            ...(hasClickId ? { click_id: clickIdParams } : {}),
        };
    }

    /**
 * 디바이스 정보 수집
 */
    private getDeviceInfo() {
        if (typeof navigator === 'undefined') {
            return { ua: '' };
        }

        return {
            ua: navigator.userAgent,
            browser: this.getBrowserName(),
            browser_version: this.getBrowserVersion(),
            os: this.getOSName(),
            os_version: this.getOSVersion(),
            device_type: this.getDeviceType(),
            locale: navigator.language || null,
            tz: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone || null : null,
            screen_w: typeof screen !== 'undefined' ? screen.width || null : null,
            screen_h: typeof screen !== 'undefined' ? screen.height || null : null,
            dpr: typeof window !== 'undefined' ? window.devicePixelRatio || null : null,
        };
    }

    private getBrowserName(): string | null {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        return null;
    }

    private getBrowserVersion(): string | null {
        // 간단한 버전 추출 (정확도보다는 구현 단순성 우선)
        const ua = navigator.userAgent;
        const match = ua.match(/(Chrome|Firefox|Safari|Edge)\/([0-9.]+)/);
        return match ? match[2] : null;
    }

    private getOSName(): string | null {
        const ua = navigator.userAgent;
        if (ua.includes('Windows')) return 'Windows';
        if (ua.includes('Mac')) return 'macOS';
        if (ua.includes('Linux')) return 'Linux';
        if (ua.includes('Android')) return 'Android';
        if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
        return null;
    }

    private getOSVersion(): string | null {
        // OS 버전 추출은 복잡하므로 기본 구현만 제공
        const ua = navigator.userAgent;
        const windowsMatch = ua.match(/Windows NT ([0-9.]+)/);
        if (windowsMatch) return windowsMatch[1];

        const macMatch = ua.match(/Mac OS X ([0-9_.]+)/);
        if (macMatch) return macMatch[1].replace(/_/g, '.');

        return null;
    }

    private getDeviceType(): 'desktop' | 'mobile' | 'tablet' | 'bot' | null {
        const ua = navigator.userAgent;

        if (/bot|crawler|spider/i.test(ua)) return 'bot';
        if (/mobile/i.test(ua)) return 'mobile';
        if (/tablet|ipad/i.test(ua)) return 'tablet';
        return 'desktop';
    }

    /**
 * 성능 정보 수집
 */
    private getPerfInfo() {
        if (typeof window === 'undefined' || typeof performance === 'undefined' || !performance.getEntriesByType) {
            return {};
        }

        try {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            const paint = performance.getEntriesByType('paint');

            const result: any = {};

            if (navigation) {
                result.ttfb_ms = Math.round(navigation.responseStart - navigation.requestStart);
            }

            const fcp = paint.find(p => p.name === 'first-contentful-paint');
            if (fcp) {
                result.fcp_ms = Math.round(fcp.startTime);
            }

            // LCP는 별도 관찰이 필요하므로 생략

            return result;
        } catch {
            return {};
        }
    }

    /**
     * 수동 플러시
     */
    async flush(): Promise<void> {
        return this.buffer.flush();
    }

    /**
     * 사용자 ID 설정
     */
    setUser(userId: string | null): void {
        setUserIdInternal(userId);
    }

    /**
     * A/B 테스트 버킷 설정
     */
    setAB(buckets: Record<string, string>): void {
        setABBuckets(buckets);
    }

    /**
     * 설정 업데이트
     */
    updateConfig(config: Partial<AnalyticsConfig>): void {
        updateConfig(config);
        setDebugMode(getConfig().debug);
    }

    /**
     * 세션 정보 반환 (디버그용)
     */
    getSessionInfo() {
        return {
            anonId: getAnonId(),
            userId: getUserId(),
            sessionId: getSessionId(),
            viewId: getViewId(),  // 🆕 현재 view_id
            abBuckets: getABBuckets(),
        };
    }
}

// 싱글톤 인스턴스
const client = new AnalyticsClient();

// 공개 API
export function initAnalytics(config: Partial<AnalyticsConfig> = {}): void {
    client.init(config);
}

export function track<T extends EventName>(
    eventName: T,
    props?: EventPropsMap[T]
): void {
    client.track(eventName, props);
}

export function flush(): Promise<void> {
    return client.flush();
}

export function setUser(userId: string | null): void {
    client.setUser(userId);
}

export function setAB(buckets: Record<string, string>): void {
    client.setAB(buckets);
}

export function updateAnalyticsConfig(config: Partial<AnalyticsConfig>): void {
    client.updateConfig(config);
}

// ID 관리 함수 재export (편의성)
export { getViewId, generateViewId, setViewId, clearViewId } from './ids';

// 페이지 액션 추적 함수 재export
export { trackPageAction } from './actions';

// 타입 내보내기
export type { AnalyticsConfig, EventName, EventPropsMap };
